import {
  Box,
  Text,
  Select,
  Tooltip,
  Badge,
  Alert,
  AlertIcon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  VStack,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';

export function useSheetContext() {
  const { activeSheet, sheets, pagination } = useSheetViewerV2();

  const currentSheetId = activeSheet?.data?.id;
  const currentPageNumber =
    pagination?.pageNumber ?? activeSheet?.data?.page ?? 1;
  const currentSheet = sheets?.find((s) => s.id === currentSheetId);
  const sheetName = currentSheet?.name ?? 'Unknown Sheet';

  return { currentSheetId, currentPageNumber, sheetName };
}

export function AdvancedAccordion({ children }: { children: React.ReactNode }) {
  return (
    <Accordion allowToggle>
      <AccordionItem border="none">
        {({ isExpanded }) => (
          <>
            <AccordionButton
              px={1}
              py={0}
              borderRadius={'md'}
              width="fit-content"
            >
              <AccordionIcon mr={1} color={'gray.500'} />
              <Text fontWeight="medium" fontSize="xs" color="gray.500">
                {isExpanded ? 'Hide Advanced' : 'Show Advanced'}
              </Text>
            </AccordionButton>
            <AccordionPanel px={0} pb={4}>
              <VStack spacing={4} align="stretch">
                {children}
              </VStack>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
}

export function useCalibrationStatus(
  sheetId: string | undefined,
  pageNumber: number
) {
  const { sheets } = useSheetViewerV2();

  return useMemo(() => {
    const sheet = sheets?.find((s) => s.id === sheetId);
    if (!sheet?.sheetPageCalibrations) return false;
    return sheet.sheetPageCalibrations.some(
      (cal) => cal.pageNumber === pageNumber && cal.calibration
    );
  }, [sheets, sheetId, pageNumber]);
}

export function TargetSheetPanel({
  hasSheet,
  sheetName,
  pageNumber,
  isCalibrated,
}: {
  hasSheet: boolean;
  sheetName: string;
  pageNumber: number;
  isCalibrated: boolean;
}) {
  if (!hasSheet) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <Text fontSize="sm">Open a sheet to detect rooms.</Text>
      </Alert>
    );
  }
  return (
    <Box
      p={2}
      bg="gray.50"
      _dark={{ bg: 'gray.900' }}
      borderRadius="md"
      borderWidth="1px"
    >
      <Text fontSize="xs" fontWeight="medium" mb={1} color="gray.500">
        Target Sheet
      </Text>
      <Tooltip label={sheetName} hasArrow>
        <Box position="relative">
          <Select
            variant="filled"
            isDisabled
            value="Current page"
            size="xs"
            pr={20}
          >
            <option value="Current page">Current page</option>
          </Select>
          <Badge
            colorScheme={isCalibrated ? 'green' : 'orange'}
            fontSize="xs"
            position="absolute"
            right={2}
            top="50%"
            transform="translateY(-50%)"
            pointerEvents="none"
          >
            {isCalibrated ? 'Calibrated' : 'Not Calibrated'}
          </Badge>
        </Box>
      </Tooltip>
      {!isCalibrated && (
        <Text fontSize="xs" color="orange.600" mt={2}>
          For best results, calibrate the sheet first.
        </Text>
      )}
    </Box>
  );
}
